import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  UserCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { reportsAPI } from '../../services/api'

// VH-55: Conversational follow-up dialog grounded in a single report.
// One thread per report. Append-only. Soft cap returned by the API.

const FALLBACK_SOFT_CAP = 50 // used only if API response omits soft_cap

export default function ReportChatPanel({ reportId, onGenerateNew }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [submitError, setSubmitError] = useState(null)
  const scrollRef = useRef(null)

  // Load chat history. Backend returns { messages, soft_cap, user_turn_count }.
  // We keep the full payload here so we honour whatever cap the server enforces.
  const { data: chat, isLoading } = useQuery({
    queryKey: ['healthReportChat', reportId],
    queryFn: () => reportsAPI.listChat(reportId),
    select: (response) => {
      const payload = response.data || {}
      // Tolerate both the new shape ({messages, soft_cap, ...}) and the
      // legacy "bare array" shape returned by older builds.
      if (Array.isArray(payload)) return { messages: payload, soft_cap: null }
      return {
        messages: payload.messages || [],
        soft_cap: payload.soft_cap ?? null,
        user_turn_count: payload.user_turn_count ?? null,
      }
    },
    enabled: !!reportId,
  })

  const messages = chat?.messages || []
  const softCap = chat?.soft_cap ?? FALLBACK_SOFT_CAP
  // Counter visible when within 10 turns of the cap (or 80% of cap, whichever is greater)
  const counterVisibleAt = Math.max(softCap - 10, Math.floor(softCap * 0.8))

  // Send mutation
  const sendMutation = useMutation({
    mutationFn: (content) => reportsAPI.sendChat(reportId, content),
    onSuccess: () => {
      setDraft('')
      setSubmitError(null)
      queryClient.invalidateQueries({ queryKey: ['healthReportChat', reportId] })
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail
      setSubmitError(typeof detail === 'string' ? detail : t('aiAnalysis.chat.errorRetry'))
    },
  })

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length, sendMutation.isPending])

  // Prefer server-provided count (authoritative); fall back to local count if absent.
  const userTurnCount = chat?.user_turn_count ?? messages.filter((m) => m.role === 'user').length
  const atCap = userTurnCount >= softCap
  const showCounter = userTurnCount >= counterVisibleAt
  const submitting = sendMutation.isPending

  const canSubmit = !submitting && !atCap && draft.trim().length > 0

  const handleSubmit = (e) => {
    e?.preventDefault()
    if (!canSubmit) return
    sendMutation.mutate(draft.trim())
  }

  const handleKeyDown = (e) => {
    // Enter to submit, Shift+Enter for newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="border rounded-lg bg-surface-container mt-6 flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 mr-2" />
            {t('aiAnalysis.chat.title')}
          </h4>
          {showCounter && (
            <span className={`text-xs ${atCap ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {t('aiAnalysis.chat.messageCount', { used: userTurnCount, cap: softCap })}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-1">{t('aiAnalysis.chat.subtitle')}</p>
      </div>

      {/* Disclaimer banner */}
      <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-800 flex items-start">
        <ExclamationTriangleIcon className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" />
        <span>{t('aiAnalysis.chat.disclaimer')}</span>
      </div>

      {/* Message history */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[480px] min-h-[200px]"
      >
        {isLoading ? (
          <div className="text-center text-sm text-gray-400 py-6">
            <ArrowPathIcon className="h-5 w-5 inline animate-spin mr-1" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            {t('aiAnalysis.chat.empty')}
          </p>
        ) : (
          messages.map((msg) => <ChatBubble key={msg.id} message={msg} />)
        )}

        {/* In-flight indicator */}
        {submitting && (
          <div className="flex items-center text-sm text-gray-500">
            <SparklesIcon className="h-4 w-4 mr-2 text-purple-500 animate-pulse" />
            {t('aiAnalysis.chat.thinking')}
          </div>
        )}
      </div>

      {/* Inline error */}
      {submitError && !submitting && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-xs text-red-700 flex items-center justify-between">
          <span className="flex items-center">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />
            {submitError}
          </span>
          <button
            type="button"
            onClick={() => sendMutation.mutate(draft.trim() || messages[messages.length - 1]?.content || '')}
            className="text-red-700 underline text-xs ml-2"
          >
            {t('aiAnalysis.retry')}
          </button>
        </div>
      )}

      {/* Composer (sticky on mobile) */}
      <div className="border-t p-3 sticky bottom-0 bg-surface-container rounded-b-lg pb-[env(safe-area-inset-bottom,0.75rem)]">
        {atCap ? (
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-700">
              {t('aiAnalysis.chat.capReached', { cap: softCap })}
            </p>
            {onGenerateNew && (
              <button
                type="button"
                onClick={onGenerateNew}
                className="text-purple-600 hover:text-purple-700 text-sm underline"
              >
                {t('aiAnalysis.chat.generateNew')}
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={submitting}
              rows={1}
              placeholder={t('aiAnalysis.chat.placeholder')}
              className="flex-1 resize-none px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50"
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={!canSubmit}
              className={`p-2 rounded-md transition-colors flex-shrink-0 ${
                canSubmit
                  ? 'bg-purple-600 text-on-neutral hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              aria-label={t('aiAnalysis.chat.send')}
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function ChatBubble({ message }) {
  const isUser = message.role === 'user'
  const Icon = isUser ? UserCircleIcon : SparklesIcon

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`flex items-start max-w-[85%] sm:max-w-[75%] ${
          isUser ? 'flex-row-reverse' : 'flex-row'
        }`}
      >
        <div
          className={`flex-shrink-0 ${isUser ? 'ml-2' : 'mr-2'} ${
            isUser ? 'text-purple-600' : 'text-gray-500'
          }`}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div
          className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
            isUser
              ? 'bg-purple-600 text-on-neutral rounded-tr-sm'
              : 'bg-gray-100 text-gray-800 rounded-tl-sm'
          }`}
        >
          {message.content}
        </div>
      </div>
    </div>
  )
}
